import GoogleMapReact, { ChangeEventValue } from 'google-map-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { defaultCoordinates, defaultGoogleMapOptions, googleMapBootstrapURLKeys } from 'src/constants/map';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import client from 'src/services/client';
import { LatLng } from 'src/types/main';
import { throttle } from 'src/utils/perfomance';
import AddressSearch from 'src/view/components/AddressSearch/AddressSearch';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import Icon from 'src/view/components/Controls/Icon/Icon';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import MapZoom from 'src/view/components/Map/MapZoom/MapZoom';
import PlacePosition from 'src/view/containers/Modals/BranchLocationModal/components/PlacePosition/PlacePosition';
import { BUTTON_TITLE } from '~/constants/main';
import logger from '~/services/logger';
import './style.scss';

/**
 * Branch location modal map
 */
const BranchLocationModal = (): JSX.Element => {
  const predefinedParams = {
    address: modalStore.branchLocationModal?.address,
    lat: modalStore.branchLocationModal?.lat,
    lng: modalStore.branchLocationModal?.lng,
  };

  const [zoom, setZoom] = useState<number>(5);
  const [loading, setLoading] = useState<boolean>(false);
  const [address, setAddress] = useState<string>('');
  const [location, setLocation] = useState<LatLng | null>(null);

  useEffect(() => {
    // set closer zoom if location is already defined
    if (predefinedParams.lat && predefinedParams.lng) setZoom(16);
  }, []);

  const onSave = () => {
    if (location && address) {
      modalStore.branchLocationModal?.onSave({ latitude: location.lat, longitude: location.lng, address });
    }

    modalStore.closeBranchLocationModal();
  };

  /**
   * Callback on search select address
   */
  const onChangeAddress = (addr: string, coords?: LatLng) => {
    setAddress(addr);
    if (coords) setLocation(coords);
  };

  const onZoomIn = () => {
    setZoom((prev) => {
      // handle max zooming limit
      return !defaultGoogleMapOptions.maxZoom || prev < defaultGoogleMapOptions.maxZoom ? prev + 1 : prev;
    });
  };

  const onZoomOut = () => {
    setZoom((prev) => {
      // handle min zooming limit
      return !defaultGoogleMapOptions.minZoom || prev > defaultGoogleMapOptions.minZoom ? prev - 1 : prev;
    });
  };

  /**
   * Get location and address on each map changing
   *
   * @param center change event obj
   */
  const getAddressByLocationCoords = async ({ center }: ChangeEventValue) => {
    try {
      setLocation({ lat: center.lat, lng: center.lng });

      setLoading(true);
      const res = await client.get('v3/delivery/address', {
        params: { latitude: center.lat, longitude: center.lng },
      });

      setAddress(res.address);
    } catch (error: any) {
      logger.log(error);
    } finally {
      setLoading(false);
    }
  };

  const onChangePosition = useCallback(throttle(getAddressByLocationCoords, 1000), []);

  const positionCoords = useMemo(() => {
    // Return new location if it was initialized
    if (location) return { lat: location.lat, lng: location.lng };

    // Return predefined location
    const { lat, lng } = predefinedParams;
    if (lat && lng) return { lat, lng };

    return defaultCoordinates;
  }, [location]);

  return (
    <CustomModalComponent modalMaxWidth={1074} isOpen onRequestClose={modalStore.closeBranchLocationModal}>
      <ModalBackground className="d-flex full-height">
        <div className="branch-location-modal">
          <GoogleMapReact
            zoom={zoom}
            bootstrapURLKeys={googleMapBootstrapURLKeys()}
            center={positionCoords}
            yesIWantToUseGoogleMapApiInternals
            options={defaultGoogleMapOptions}
            onChange={onChangePosition}
          />

          <PlacePosition address={address} />

          <MapZoom onZoomIn={onZoomIn} onZoomOut={onZoomOut} />

          <Icon name={Icons.Close} size={24} className="branch-location-modal__close-icon" onPress={modalStore.closeBranchLocationModal} />

          <AddressSearch
            value={address}
            onChangeAddress={onChangeAddress}
            loading={loading}
            placeholder="txt_search_address"
            hideSearchIcon={false}
            className="branch-location-modal__search-wrapper"
            listWrapperClassName="branch-location-modal__search-list-wrapper"
            contentClassNameForInput="branch-location-modal__search"
          />

          <Button
            title={localeStore.t(BUTTON_TITLE.SAVE)}
            onClick={onSave}
            loading={loading}
            size="large"
            className="branch-location-modal__save-btn"
          />
        </div>
      </ModalBackground>
    </CustomModalComponent>
  );
};

export default BranchLocationModal;
